import type PIXI from 'pixi.js';
import { RenderTexture } from 'pixi.js';

const DATA_URL_PREFIX = 'data:image/png;base64,';

export const takeGraphScreenshot = (app: PIXI.Application): string =>
  extractBase64WithMaxDimension(app).replace(DATA_URL_PREFIX, '');

/**
 * Extracts a base64 WebP image from the PIXI stage, scaling it down
 * to a maximum dimension while keeping the aspect ratio.
 */
function extractBase64WithMaxDimension(
  app: PIXI.Application,
  maxDimension = 1024,
): string {
  // Get the bounds of the entire stage
  const bounds = app.stage.getBounds();

  // Calculate the scaling factor to fit within maxDimension
  let scale = 1;
  if (bounds.width > maxDimension || bounds.height > maxDimension) {
    scale =
      bounds.width > bounds.height
        ? maxDimension / bounds.width
        : maxDimension / bounds.height;
  }

  // Determine the new width and height for the render texture
  const targetWidth = Math.floor(bounds.width * scale);
  const targetHeight = Math.floor(bounds.height * scale);

  // Create a render texture with the calculated dimensions
  const renderTexture = RenderTexture.create({
    width: targetWidth,
    height: targetHeight,
  });

  // Save the current stage scale and position
  const originalScaleX = app.stage.scale.x;
  const originalScaleY = app.stage.scale.y;
  const originalPositionX = app.stage.position.x;
  const originalPositionY = app.stage.position.y;

  // Scale and center the stage to fit within the render texture
  app.stage.scale.set(scale, scale);
  app.stage.position.set(-bounds.x * scale, -bounds.y * scale);

  // Render the stage to the render texture
  app.renderer.render(app.stage, renderTexture);

  // Reset the stage scale and position to their original values
  app.stage.scale.set(originalScaleX, originalScaleY);
  app.stage.position.set(originalPositionX, originalPositionY);

  // Extract the image as a base64 string from the render texture
  const base64Image = app.renderer.plugins.extract.base64(
    renderTexture,
    'image/png',
  );

  // Destroy the render texture to free up resources
  renderTexture.destroy(true);

  return base64Image;
}

export const graphScreenshotToDataUrl = (screenshot: string): string =>
  `${DATA_URL_PREFIX}${screenshot}`;
